import React from 'react';
import { Clock, Bookmark, BookmarkCheck, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export function ContentView({
  section,
  isCompleted,
  isBookmarked,
  onToggleBookmark,
  onMarkComplete,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}) {
  if (!section) {
    return (
      <div data-testid="content-empty" className="flex-1 flex items-center justify-center p-12">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-blue-50 dark:bg-blue-950 flex items-center justify-center">
            <img src="/servicenow-logo.png" alt="ServiceNow" className="h-8 w-auto" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
            ServiceNow Admin Training
          </h2>
          <p className="text-zinc-500 dark:text-zinc-400 text-sm leading-relaxed mb-6">
            Select a topic from the sidebar to begin your learning journey. Track your progress, bookmark important sections, and test your knowledge with quizzes.
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-zinc-400">
            <span>Powered by</span>
            <img src="/steadfast-logo.png" alt="Steadfast" className="h-6 w-auto" />
          </div>
        </div>
      </div>
    );
  }

  const completed = isCompleted(section.id);
  const bookmarked = isBookmarked(section.id);

  return (
    <div data-testid="content-view" className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8 md:py-12">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            {section.moduleName && (
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-500 mb-2">
                {section.moduleName} &mdash; {section.topicName}
              </p>
            )}
            <h1
              data-testid="content-title"
              className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight leading-tight"
            >
              {section.title}
            </h1>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-1">
            {section.readingTime && (
              <Badge variant="outline" className="flex items-center gap-1 text-xs">
                <Clock className="h-3 w-3" />
                {section.readingTime} min
              </Badge>
            )}
            <Button
              data-testid="bookmark-toggle"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onToggleBookmark(section.id)}
            >
              {bookmarked ? (
                <BookmarkCheck className="h-4 w-4 text-blue-500" />
              ) : (
                <Bookmark className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <article
          data-testid="content-body"
          className="prose-content"
          dangerouslySetInnerHTML={{ __html: section.content }}
        />

        <div className="mt-12 pt-6 border-t border-zinc-200 dark:border-zinc-800 flex items-center justify-between gap-4">
          <div>
            {hasPrev && (
              <Button
                data-testid="prev-section-btn"
                variant="outline"
                size="sm"
                onClick={onPrev}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!completed ? (
              <Button
                data-testid="mark-complete-btn"
                onClick={() => onMarkComplete(section.id)}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Mark Complete
              </Button>
            ) : (
              <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Completed
              </Badge>
            )}
          </div>

          <div>
            {hasNext && (
              <Button
                data-testid="next-section-btn"
                variant="outline"
                size="sm"
                onClick={onNext}
                className="flex items-center gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between text-xs text-zinc-400">
          <div className="flex items-center gap-2">
            <img src="/steadfast-logo.png" alt="Steadfast" className="h-5 w-auto opacity-40" />
            <span>&times;</span>
            <img src="/servicenow-logo.png" alt="ServiceNow" className="h-4 w-auto opacity-40" />
          </div>
        </div>
      </div>
    </div>
  );
}
